/**
 * TODO: criar atributo validator para rodar validação no componente Input
 * quando defaultValue vem preenchido
 */

import React, { useState, useRef, forwardRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import ptBr from 'date-fns/locale/pt-BR'
import classNames from 'classnames';
import { Button, Icon } from '../';
import creditCardType from 'credit-card-type';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import { maskPhone } from '../../utils/Masks';
import {
  normalizeWhatsAppToE164,
  isBrazilianWhatsAppE164,
  getIntlTelValidationMessage,
  cpfCnpjFormat,
} from '../../utils/Formats';

import 'react-datepicker/dist/react-datepicker.css';
import './styles.scss';

// Compat: alguns trechos antigos do arquivo referenciavam `getIntlValidationMessage`.
// Mantemos um alias para evitar erro no ESLint durante transições.
const getIntlValidationMessage = getIntlTelValidationMessage;

/**
 * Valida com libphonenumber (intl-tel-input utils) para qualquer país, inclusive BR (+55).
 * lenientIncomplete: no input, não mostrar erro enquanto o número ainda está "curto" (TOO_SHORT).
 */
const applyPhoneIntlValidity = (iti, inputEl, intlTelInputModule, { lenientIncomplete = false } = {}) => {
  const countryData = iti.getSelectedCountryData();
  if (!countryData || !countryData.dialCode) return;

  const rawDigits = String(inputEl.value || '').replace(/\D/g, '');
  if (!rawDigits) {
    inputEl.setCustomValidity('');
    return;
  }

  if (!intlTelInputModule.utils) {
    inputEl.setCustomValidity('');
    return;
  }

  const valid = iti.isValidNumber();
  if (valid === null) {
    inputEl.setCustomValidity('');
    return;
  }
  if (valid === true) {
    inputEl.setCustomValidity('');
    return;
  }

  const err = iti.getValidationError();
  const ve = intlTelInputModule.utils.validationError;
  if (lenientIncomplete && ve && err === ve.TOO_SHORT) {
    inputEl.setCustomValidity('');
    return;
  }
  inputEl.setCustomValidity(getIntlValidationMessage(intlTelInputModule, err));
};

/** Aplica valor inicial/atualizado sem usar o país padrão do plugin (ex.: BR) para decidir a máscara. */
const applyPhoneInternationalValue = (iti, inputEl, rawValue) => {
  const e164 = normalizeWhatsAppToE164(rawValue);
  if (!e164) {
    iti.setNumber('');
    return;
  }
  if (isBrazilianWhatsAppE164(e164)) {
    const national = e164.replace(/^\+55/, '').replace(/\D/g, '').slice(0, 11);
    const masked = maskPhone(national);
    inputEl.value = masked;
    iti.setNumber(e164);
  } else {
    iti.setNumber(e164);
  }
};

const Input = forwardRef(({ variant, size, label, ...props }, ref) => {
  const { className, placeholder, type } = props
  const prefix = 'form-control';
  const newClassName = classNames(prefix, variant && `${prefix}-${variant}`, size && `${prefix}-${size}`, className);

  return (<>
    <input ref={ref} {...props}
      type={type || 'text'}
      className={newClassName}
      placeholder={(!label && placeholder) || (label && ` `)}
    />
    {label && <label className="form-label" htmlFor={props.id}>
      {label}
    </label>}
  </>);
});

const InputEmail = forwardRef((props, ref) => {
  // TODO: fix pattern [a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}"

  return (
    <Input ref={ref} {...props} type="email" />
  );
});

const InputTel = forwardRef((props, ref) => {
  const setMask = (event) => {
    const target = event.target;
    target.value = target.value.replace(/\D/g, '');
  }

  const handleChange = (event) => {
    setMask(event)

    if (props.onChange) {
      props.onChange(event)
    }
  }

  return (
    <Input ref={ref} {...props} onChange={handleChange} type="tel" />
  );
});

const InputPhone = forwardRef((props, ref) => {
  const inputRef = useRef()

  const setMask = (event) => {
    const target = event.target;
    let value = target.value.replace(/\D/g, '')
      .replace(/^(\d{1})/, '($1')
      .replace(/^(\(\d{2})(\d)/, '$1) $2')
      .replace(/(\d)(\d{4})$/, '$1-$2');

    target.value = value
  }

  const handleChange = (event) => {
    setMask(event)

    if (props.onChange) {
      props.onChange(event)
    }
  }

  return (
    <InputTel ref={inputRef} {...props} onChange={handleChange} maxLength="15" pattern="\([0-9]{2}\) [0-9]{4,5}-[0-9]{4}" />
  );
});

const InputCpf = forwardRef((props, ref) => {
  const setMask = (event) => {
    const target = event.target;
    const value = target.value.replace(/\D/g, '')
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2')

    target.value = value
  }

  const validator = (value) => {
    const cpf = value.replace(/[^\d]+/g, '');
    if (cpf === '') return false;
    const fd = cpf.substring(0, 9), ld = cpf.substring(9, 11);
    for (let v = 0; 10 > v; v++) if ('' + fd + ld === '' + v + v + v + v + v + v + v + v + v + v + v) return false;
    let add = 0, rev = 0;
    for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
    rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(9))) return false;
    add = 0;
    for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
    rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(10))) return false;
    return true;
  }

  const handleChange = (event) => {
    const target = event.target

    setMask(event)

    if (!validator(target.value)) {
      target.setCustomValidity('Número de CPF inválido. Insira um CPF válido.')
    } else {
      target.setCustomValidity('')
    }

    if (props.onChange) {
      props.onChange(event)
    }
  }

  return (
    <InputTel ref={ref} {...props}
      maxLength="14"
      pattern="[0-9]{3}\.[0-9]{3}\.[0-9]{3}-[0-9]{2}"
      onChange={handleChange}
    />
  );
});

const validateCpfDigits = (cpf) => {
  if (cpf.length !== 11) return false
  if (/^(\d)\1{10}$/.test(cpf)) return false
  let add = 0
  let rev = 0
  for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i), 10) * (10 - i)
  rev = 11 - (add % 11)
  if (rev === 10 || rev === 11) rev = 0
  if (rev !== parseInt(cpf.charAt(9), 10)) return false
  add = 0
  for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i), 10) * (11 - i)
  rev = 11 - (add % 11)
  if (rev === 10 || rev === 11) rev = 0
  if (rev !== parseInt(cpf.charAt(10), 10)) return false
  return true
}

const validateCnpjDigits = (cnpj) => {
  if (cnpj.length !== 14) return false
  if (/^(\d)\1{13}$/.test(cnpj)) return false
  let sum = 0
  let weight = 2
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(cnpj.charAt(i), 10) * weight
    weight++
    if (weight === 10) weight = 2
  }
  let remainder = sum % 11
  const digit1 = remainder < 2 ? 0 : 11 - remainder
  if (digit1 !== parseInt(cnpj.charAt(12), 10)) return false
  sum = 0
  weight = 2
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(cnpj.charAt(i), 10) * weight
    weight++
    if (weight === 10) weight = 2
  }
  remainder = sum % 11
  const digit2 = remainder < 2 ? 0 : 11 - remainder
  if (digit2 !== parseInt(cnpj.charAt(13), 10)) return false
  return true
}

const applyCpfCnpjValidity = (target) => {
  const clean = target.value.replace(/\D/g, '')
  if (clean.length === 0) {
    target.setCustomValidity('')
    return
  }
  if (clean.length < 11) {
    target.setCustomValidity('Informe o CPF (11 dígitos) ou CNPJ (14 dígitos).')
    return
  }
  if (clean.length === 11) {
    target.setCustomValidity(
      validateCpfDigits(clean)
        ? ''
        : 'CPF inválido. Verifique os números digitados.'
    )
    return
  }
  if (clean.length < 14) {
    target.setCustomValidity('CNPJ incompleto. São necessários 14 dígitos.')
    return
  }
  target.setCustomValidity(
    validateCnpjDigits(clean)
      ? ''
      : 'CNPJ inválido. Verifique os números digitados.'
  )
}

const InputCpfCnpj = forwardRef((props, ref) => {
  const setMask = (event) => {
    const target = event.target
    const formatted = cpfCnpjFormat(target.value)
    target.value = formatted === undefined || formatted === '' ? '' : formatted
  }

  const handleChange = (event) => {
    const target = event.target
    setMask(event)
    applyCpfCnpjValidity(target)
    if (props.onChange) {
      props.onChange(event)
    }
  }

  return (
    <InputTel
      ref={ref}
      {...props}
      maxLength="18"
      onChange={handleChange}
    />
  );
});

const InputPassword = forwardRef((props, ref) => {
  const [passwordShown, setPasswordShown] = useState(false)

  const handleToggle = () => {
    setPasswordShown(!passwordShown)
  }

  return (<>
    <Input ref={ref} {...props}
      type={passwordShown ? 'text' : 'password'}
      minLength="8"
    />
    <Button variant="action" onClick={handleToggle} tabIndex="-1">
      {passwordShown ?
        <Icon id="icon-visibility_off" size="24" />
        :
        <Icon id="icon-visibility" size="24" />
      }
    </Button>
  </>);
});

// Uncontrolled by default (an expiration from tomorrow on, with a time). `selected` makes it
// controlled, `dateOnly` drops the time, and `minDate` (null for none) replaces tomorrow.
const InputDate = forwardRef(({ onChange, selected, minDate, dateOnly, ...props }, ref) => {
  const now = new Date()
  const [startDate, setStartDate] = useState(now)
  const tomorrow = now.setDate(now.getDate() + 1)

  const handleChange = (date) => {
    setStartDate(date)

    if (onChange) {
      onChange(date)
    }

    if (ref?.current?.handleCalendarClickOutside) {
      ref.current.handleCalendarClickOutside()
    }
  }

  return (
    <DatePicker ref={ref}
      name={props.name}
      id={props.id}
      showTimeSelect={!dateOnly}
      timeCaption="Hora"
      dateFormat={dateOnly ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm:ss'}
      closeOnScroll={true}
      selected={selected === undefined ? startDate : selected}
      locale={ptBr}
      customInput={<Input {...props} />}
      minDate={minDate === undefined ? tomorrow : minDate}
      onChange={handleChange}
    />
  );
});

const InputFile = forwardRef((props, ref) => {
  return (
    <Input ref={ref} {...props} type="file" />
  );
});

const InputSearch = forwardRef((props, ref) => {
  return (
    <Input ref={ref} {...props} type="search" />
  );
});

const InputCreditCard = forwardRef(({ onChange, ...props }, ref) => {
  const [cardType, setCardType] = useState(null);

  const creditCardValidator = (event) => {
    let cardNumber = event.target.value;
    cardNumber = cardNumber.replace(/\D/g, '');
    cardNumber = cardNumber.replace(/(\d{4})(\d{1,4})?(\d{1,4})?(\d{1,4})?/, '$1 $2 $3 $4');

    const types = creditCardType(cardNumber);

    if (types.length) {
      setCardType(types[0].type);
    } else {
      setCardType(null);
    }

    if (onChange) {
      event.target.value = cardNumber;
      onChange(event);
    }
  };

  useEffect(() => {
    if (props.defaultValue) {
      creditCardValidator({ target: { value: props.defaultValue } });
    }
  }, [props.defaultValue]);

  return (
      <div className="input-credit-card">
        <Input {...props} ref={ref} type="tel" onChange={creditCardValidator} maxLength={19} />
        {cardType && (
            <Icon id={'icon-' + cardType} className="icon" size="30" />
        )}
      </div>
  );
});

const InputCheckbox = forwardRef(({ children, ...props }, ref) => {
  var newClassNames = classNames('form-check-input', props.className)
  return (
    <input ref={ref} {...props} type="checkbox" className={newClassNames} />
  );
});

const InputRadio = forwardRef(({ children, ...props }, ref) => {
  return (
    <input ref={ref} {...props} type="radio" className="form-check-input" />
  );
});

const InputSwitch = forwardRef(({ children, labelOn, labelOff, checked, defaultChecked, ...props }, ref) => {
  const inputProps = {
    ...props,
    ref,
    type: "checkbox",
  }

  if (checked !== undefined) {
    inputProps.checked = checked
  } else if (defaultChecked !== undefined) {
    inputProps.defaultChecked = defaultChecked
  }

  return (
    <label className="control-switch">
      <input {...inputProps} />
      {labelOn && labelOff &&
        <span className="control-switch-label" data-label-on={labelOn} data-label-off={labelOff}></span>
      }
      <span className="control-switch-inner">
        <span className="control-switch-bullet"></span>
      </span>
    </label>
  );
});

const InputGroup = (props) => {
  return (
    <div {...props} className="input-group" />
  )
}

const InputVariable = forwardRef(({ variables, onChange, name, ...props }, ref) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [finalValue, setFinalValue] = useState("");

  const handleVariableClick = (variable) => {
    if (!selectedVariables.includes(variable)) {
      setSelectedVariables((prev) => [...prev, variable]);
    }
  };

  const handleRemoveVariable = (variableToRemove) => {
    setSelectedVariables((prev) =>
      prev.filter((variable) => variable !== variableToRemove)
    );
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const calculateFinalValue = () => {
    let value = inputValue;

    selectedVariables.forEach((variable) => {
      value = value + ` <#${variable === 'TemplateEditor' ? 'Preparador do documento' : variable}#>`;
    });

    return value;
  };

  useEffect(() => {
    const updatedValue = calculateFinalValue();
    setFinalValue(updatedValue);

    // Chama o onChange passando o valor final calculado junto com o "name"
    if (onChange) {
      onChange({
        target: {
          name: name, // Incluindo o "name" aqui
          value: updatedValue
        }
      });
    }
  }, [inputValue, selectedVariables]);

  return (
    <div>
      <div className="input-variables-wrapper">
        <Input
          ref={ref}
          {...props}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Digite algo ou adicione variáveis"
        />
        
        <div className="variables-boxes">
          {selectedVariables.map((variable, index) => (
            <span key={index} className="variable-box">
              {`<#${variable}#>`}
              <Button onClick={() => handleRemoveVariable(variable)} variant="remove-icon">
                <Icon id="icon-close" size="14" />
              </Button>
            </span>
          ))}
        </div>
      </div>

      <div className="input-variables">
        <p className="title">Variáveis</p>
        <ul className="list-variables">
          {variables.map((variable) => (
            <li
              key={variable}
              onClick={() => handleVariableClick(variable)}
              className="item-variable"
            >
              {`<#${variable}#>`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});

const InputPhoneInternational = forwardRef(({ onChange, defaultValue, name, onBlur, ...props }, ref) => {
  const inputRef = useRef(null);
  const itiRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const nameRef = useRef(name);
  const onBlurRef = useRef(onBlur);

  useEffect(() => {
    onChangeRef.current = onChange;
    nameRef.current = name;
    onBlurRef.current = onBlur;
  }, [onChange, name, onBlur]);

  const combinedRef = (node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  useEffect(() => {
    if (!inputRef.current) return;

    itiRef.current = intlTelInput(inputRef.current, {
      initialCountry: 'br',
      countryOrder: ['br', 'us'],
      separateDialCode: true,
      nationalMode: false,
      formatAsYouType: true,
      formatOnDisplay: true,
      strictMode: true, // dynamic maxlength
      loadUtils: () => import('intl-tel-input/utils'),
    });

    const syncValueFromProps = () => {
      if (!itiRef.current || !inputRef.current) return;
      if (defaultValue) {
        applyPhoneInternationalValue(itiRef.current, inputRef.current, defaultValue);
      }
    };

    syncValueFromProps();
    if (itiRef.current.promise) {
      itiRef.current.promise.then(syncValueFromProps).catch(() => {});
    }

    const handleInput = (e) => {
      if (!itiRef.current || !inputRef.current) return;

      const countryData = itiRef.current.getSelectedCountryData();
      
      if (onChangeRef.current) {
        const numberFromPlugin = itiRef.current.getNumber();
        const rawDigits = String(e?.target?.value || '').replace(/\D/g, '');

        // Evita conflito entre máscara manual BR e formatação do plugin.
        const number =
          numberFromPlugin ||
          (countryData?.dialCode ? `+${countryData.dialCode}${rawDigits}` : rawDigits);

        onChangeRef.current({
          target: {
            name: nameRef.current,
            value: number
          }
        });
      }
      if (intlTelInput.utils) {
        applyPhoneIntlValidity(itiRef.current, inputRef.current, intlTelInput, {
          lenientIncomplete: true,
        });
      }
    };

    inputRef.current.addEventListener('input', handleInput);

    const handleBlur = (e) => {
      if (itiRef.current && inputRef.current) {
        applyPhoneIntlValidity(itiRef.current, inputRef.current, intlTelInput, {
          lenientIncomplete: false,
        });
      }
      if (onBlurRef.current) {
        onBlurRef.current(e);
      }
    };

    inputRef.current.addEventListener('blur', handleBlur);

    const handleCountryChange = () => {
      if (!itiRef.current || !inputRef.current) return;
      
      const countryData = itiRef.current.getSelectedCountryData();
      
      if (onChangeRef.current) {
        const numberFromPlugin = itiRef.current.getNumber();
        const rawDigits = String(inputRef.current.value || '').replace(/\D/g, '');

        const number =
          numberFromPlugin ||
          (countryData?.dialCode ? `+${countryData.dialCode}${rawDigits}` : rawDigits);

        onChangeRef.current({
          target: {
            name: nameRef.current,
            value: number
          }
        });
      }
      inputRef.current?.setCustomValidity('');
    };

    inputRef.current.addEventListener('countrychange', handleCountryChange);

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('input', handleInput);
        inputRef.current.removeEventListener('blur', handleBlur);
        inputRef.current.removeEventListener('countrychange', handleCountryChange);
      }
      if (itiRef.current) {
        itiRef.current.destroy();
        itiRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!itiRef.current || !inputRef.current) return;

    const run = () => {
      if (!itiRef.current || !inputRef.current) return;
      if (defaultValue) {
        applyPhoneInternationalValue(itiRef.current, inputRef.current, defaultValue);
      } else {
        itiRef.current.setNumber('');
      }
    };

    if (itiRef.current.promise) {
      itiRef.current.promise.then(run).catch(run);
    } else {
      run();
    }
  }, [defaultValue]);

  const handleChange = (event) => {
    if (onChangeRef.current) {
      onChangeRef.current(event);
    }
  };

  return (
    <Input
      ref={combinedRef}
      {...props}
      name={name}
      type="tel"
      onChange={handleChange}
      defaultValue={defaultValue}
    />
  );
});


Input.Tel = InputTel;
Input.Email = InputEmail;
Input.Phone = InputPhone;
Input.PhoneInternational = InputPhoneInternational;
Input.Cpf = InputCpf;
Input.CpfCnpj = InputCpfCnpj;
Input.Password = InputPassword;
Input.Date = InputDate;
Input.Search = InputSearch;
Input.CreditCard = InputCreditCard;
Input.File = InputFile;
Input.Checkbox = InputCheckbox;
Input.Radio = InputRadio;
Input.Switch = InputSwitch;
Input.Group = InputGroup;
Input.Variable = InputVariable;

export default Input;
